import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import dayjs from 'dayjs';
import { type PaymentRecordPayload } from './types';
import {
  modeOfStudyOptions,
  programmeOptions,
  type PAYMENT_TYPE,
} from '../../../components';
import { getOptionsLabel } from '../../../../Monitor/components';
import { checkInvalidDate } from '../../ApplicationForm';

const GET_PAYMENT_RECORD_PAYLOAD = gql`
  query ($applicationId: String!, $type: PAYMENT_TYPE!) {
    programmeChoiceMetadata {
      departmentList {
        value
        label
      }
    }

    getPaymentRecordPayload(applicationId: $applicationId, paymentType: $type) {
      paymentType
      applicationNo
      entryYear
      department
      programme
      studyMode
      paymentDeadline
      paymentStatus
      redirectUrl
      paymentRedirectUrlInfo
    }
  }
`;

type GetPaymentRecordPayload = {
  paymentRecord: PaymentRecordPayload;
  loading: boolean;
  error: Error | undefined;
};

export const useGetPaymentRecordPayload = (
  applicationId: string,
  type: PAYMENT_TYPE,
): GetPaymentRecordPayload => {
  const { data, loading, error } = useQuery(GET_PAYMENT_RECORD_PAYLOAD, {
    variables: { applicationId, type },
    skip: !applicationId,
    fetchPolicy: 'network-only',
  });

  const departmentList = data?.programmeChoiceMetadata?.departmentList ?? [];

  const paymentRecord: PaymentRecordPayload = {
    paymentType: data?.getPaymentRecordPayload?.paymentType,
    applicationNo: data?.getPaymentRecordPayload?.applicationNo,
    entryYear: data?.getPaymentRecordPayload?.entryYear,
    department: getOptionsLabel(
      departmentList,
      data?.getPaymentRecordPayload?.department,
    ),
    programme: getOptionsLabel(
      programmeOptions,
      data?.getPaymentRecordPayload?.programme,
    ),
    studyMode: getOptionsLabel(
      modeOfStudyOptions,
      data?.getPaymentRecordPayload?.studyMode,
    ),
    paymentDeadline: checkInvalidDate(
      data?.getPaymentRecordPayload?.paymentDeadline,
    )
      ? 'N/A'
      : dayjs(data?.getPaymentRecordPayload?.paymentDeadline).format(
          'DD/MM/YYYY',
        ),
    paymentStatus: data?.getPaymentRecordPayload?.paymentStatus,
    redirectUrl: data?.getPaymentRecordPayload?.redirectUrl,
    paymentRedirectUrlInfo:
      data?.getPaymentRecordPayload?.paymentRedirectUrlInfo,
  };

  return { paymentRecord, loading, error };
};
